import { api, primeCsrf } from './client'
import type { AuthUser } from '@/types/auth'

export interface LoginPayload {
  email: string
  password: string
  /** "Keep me signed in" — a long-lived remember-me cookie instead of a browser-session login. */
  remember?: boolean
}

export async function login(payload: LoginPayload): Promise<{ user: AuthUser } | { requires_otp: true; otp_token: string }> {
  await primeCsrf()
  const res = await api.post('/api/v1/auth/login', payload)
  return res.data
}

/** Second factor at sign-in: a 6-digit authenticator code, or (with `recovery`) a one-time recovery code. */
export async function verify2fa(otp_token: string, code: string, recovery = false): Promise<{ verified: true; user: AuthUser; recovery_codes_left?: number }> {
  await primeCsrf()
  const res = await api.post('/api/v1/auth/2fa/verify', recovery ? { otp_token, recovery_code: code } : { otp_token, code })
  return res.data
}

// ─── Two-step verification self-service (E-UX) ───────────────────────────
export interface TwoFactorStatus {
  enabled: boolean
  pending: boolean
  enabled_at: string | null
  recovery_codes_left: number
}
export interface TwoFactorSetup {
  secret: string
  otpauth_uri: string
  /** Inline SVG markup of the QR code (rendered server-side from the otpauth URI). */
  qr_svg: string
}
export async function twoFactorStatus(): Promise<TwoFactorStatus> {
  return (await api.get('/api/v1/auth/2fa')).data
}
export async function twoFactorSetup(password: string): Promise<TwoFactorSetup> {
  return (await api.post('/api/v1/auth/2fa/setup', { password })).data
}
export async function twoFactorEnable(code: string): Promise<{ enabled: true; recovery_codes: string[] }> {
  return (await api.post('/api/v1/auth/2fa/enable', { code })).data
}
export async function twoFactorDisable(password: string, code: string): Promise<{ enabled: false }> {
  return (await api.post('/api/v1/auth/2fa/disable', { password, code })).data
}
export async function twoFactorRegenerateCodes(password: string): Promise<{ recovery_codes: string[] }> {
  return (await api.post('/api/v1/auth/2fa/recovery-codes', { password })).data
}
export async function revokeOtherSessions(): Promise<{ revoked: number }> {
  return (await api.post('/api/v1/auth/sessions/revoke-others')).data
}

/** Public: the organisation name + whether a logo exists, for the sign-in screen (single-organisation installs). */
export interface PublicBranding { name: string | null; has_logo: boolean; logo_url: string | null; accent_color: string | null }
export async function publicBranding(): Promise<PublicBranding> {
  return (await api.get('/api/v1/public/branding')).data
}

/** Always resolves with the same generic message — the API never reveals whether the address exists. */
export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  await primeCsrf()
  const res = await api.post('/api/v1/auth/forgot-password', { email })
  return res.data
}

export async function resetPassword(token: string, password: string, password_confirmation: string): Promise<void> {
  await primeCsrf()
  await api.post('/api/v1/auth/reset-password', { token, password, password_confirmation })
}

/** T1-S06: what a portal-invite link means right now (never throws for a dead link). */
export type InviteState = 'PENDING' | 'USED' | 'EXPIRED' | 'SUPERSEDED' | 'REVOKED' | 'PASSWORD_SET' | 'INVALID'
export interface InviteInfo {
  state: InviteState
  message: string | null
  first_name?: string
  email?: string
  org_name?: string
  expires_at?: string | null
}

export async function inspectInvite(token: string): Promise<InviteInfo> {
  await primeCsrf()
  const res = await api.post('/api/v1/auth/invite/inspect', { token })
  return res.data
}

export async function acceptInvite(token: string, password: string, password_confirmation: string): Promise<void> {
  await primeCsrf()
  await api.post('/api/v1/auth/invite/accept', { token, password, password_confirmation })
}

export async function logout(): Promise<void> {
  await api.post('/api/v1/auth/logout')
}

export async function me(): Promise<{ user: AuthUser }> {
  const res = await api.get('/api/v1/me')
  return res.data
}

/** Forced change after HR set a temporary password (clears must_change_password server-side). */
export async function changeOwnPassword(current_password: string, new_password: string, new_password_confirmation: string): Promise<void> {
  await api.post('/api/v1/auth/change-password', { current_password, new_password, new_password_confirmation })
}

/** HR: set a one-time password; the user must change it at next sign-in. */
export async function setTemporaryPassword(userId: string, password: string): Promise<{ message: string }> {
  return (await api.post(`/api/v1/auth/users/${encodeURIComponent(userId)}/temporary-password`, { password })).data
}
